import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import { Sprout, UtensilsCrossed, GraduationCap, Briefcase, Rocket } from "lucide-react";
import { Reveal } from "../landing/Reveal";

const STATS = [
  { icon: Sprout, value: 8400, label: "Farmers Supported", fmt: (v) => Math.round(v).toLocaleString("en-IN") },
  { icon: UtensilsCrossed, value: 1200000, label: "Meals Donated", fmt: (v) => `${(v / 1000000).toFixed(1)}M` },
  { icon: GraduationCap, value: 25000, label: "Students Trained", fmt: (v) => `${Math.round(v / 1000)}K+` },
  { icon: Briefcase, value: 6700, label: "Jobs Created", fmt: (v) => Math.round(v).toLocaleString("en-IN") },
  { icon: Rocket, value: 320, label: "Startups Funded", fmt: (v) => `${Math.round(v)}` },
];

const Counter = ({ target, fmt }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, target, { duration: 2, ease: "easeOut", onUpdate: setVal });
    return () => c.stop();
  }, [inView, target]);
  return (
    <span ref={ref} data-testid={`impact-counter-${target}`}>
      {fmt(val)}
    </span>
  );
};

export const GreenNova = () => (
  <section id="impact" data-testid="greennova-section" className="relative overflow-hidden py-24 sm:py-32">
    <div aria-hidden="true" className="absolute left-1/2 top-1/2 h-[440px] w-[680px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00D26A]/[0.07] blur-[160px]" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center">
        <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#00D26A]">
          GreenNova · Social Impact
        </p>
        <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Growth that counts more than revenue.
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={0.06 * i}>
            <div
              data-testid={`impact-stat-${i}`}
              className="rounded-3xl border border-white/10 bg-[#111827]/90 p-6 text-center backdrop-blur-xl transition-colors hover:border-[#00D26A]/40 sm:p-7"
            >
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-[#00D26A]/10">
                <s.icon className="h-5 w-5 text-[#00D26A]" />
              </span>
              <p className="mt-4 font-display text-2xl font-black tracking-tight text-white sm:text-3xl">
                <Counter target={s.value} fmt={s.fmt} />
              </p>
              <p className="mt-1.5 font-jbmono text-[9px] uppercase tracking-widest text-slate-500 sm:text-[10px]">
                {s.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 text-center font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
        Illustrative impact figures for demo purposes
      </p>
    </div>
  </section>
);
